import React from 'react';
import { TrafficCone, Gauge, AlertTriangle, Radio } from 'lucide-react';

const COLORS = {
  green:  { bg: '#D2F4E5', fg: '#168162', accent: '#3FC79A' },
  yellow: { bg: '#FFF1C2', fg: '#8D6912', accent: '#F6C84F' },
  red:    { bg: '#FFDADA', fg: '#A52828', accent: '#F25555' },
  gray:   { bg: '#F1F5F9', fg: '#334155', accent: '#9AAFD7' },
};

export default function TrafficCard({ data, loading }) {
  if (loading) {
    return (
      <div className="puvio-card p-5" data-testid="traffic-card-skeleton">
        <div className="puvio-skeleton h-6 w-24" />
        <div className="puvio-skeleton h-8 w-36 mt-3" />
      </div>
    );
  }
  if (!data) return null;
  const C = COLORS[data.color] || COLORS.gray;
  const isLive = data.source === 'here';
  const jamPct = data.avg_jam != null ? Math.min(100, Math.round((data.avg_jam / 10) * 100)) : null;

  return (
    <div className="puvio-card p-5 puvio-card-interactive" data-testid="traffic-card">
      <div className="flex items-start justify-between">
        <div>
          <p className="font-bold text-[11px] uppercase tracking-[0.22em] text-[var(--puvio-mint-deep)]">Traffic</p>
          <p className="text-xs text-[var(--puvio-text-2)] mt-0.5 font-medium">{isLive ? 'Live traffic' : 'Estimated for now'}</p>
        </div>
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: C.bg }}>
          <TrafficCone className="w-6 h-6" style={{ color: C.fg }} />
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-bold" style={{ background: C.bg, color: C.fg }} data-testid="traffic-badge">
          <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: C.accent }} /> {data.label}
        </span>
        {isLive && (
          <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full bg-[var(--puvio-sun)] text-[var(--puvio-sun-text)]" data-testid="traffic-live-badge">
            <Radio className="w-3 h-3" /> Live
          </span>
        )}
      </div>
      {isLive && jamPct != null && (
        <div className="mt-3 h-2 rounded-full bg-[var(--puvio-bg-soft)] overflow-hidden" data-testid="traffic-jam-bar">
          <div className="h-full rounded-full transition-all" style={{ width: `${jamPct}%`, background: C.accent }} />
        </div>
      )}
      {isLive && (
        <div className="mt-3 flex items-center gap-3 text-[11px] text-[var(--puvio-text-2)] flex-wrap font-semibold" data-testid="traffic-stats">
          {data.avg_speed_kmh != null && <span className="inline-flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5" /> {data.avg_speed_kmh} km/h</span>}
          {data.avg_jam != null && <span>Jam {data.avg_jam}/10</span>}
          {data.samples != null && <span>{data.samples} segs</span>}
          {data.incidents > 0 && (
            <span className="inline-flex items-center gap-1 text-[var(--puvio-coral-deep)]"><AlertTriangle className="w-3.5 h-3.5" /> {data.incidents}</span>
          )}
        </div>
      )}
    </div>
  );
}
